import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { m } from 'framer-motion';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import config from '../../config';           // ← assure-toi que ce fichier existe
import { fadeIn } from '../../Functions/GlobalAnimations';

export default function PaymentPendingSubscription() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const subId  = searchParams.get('subId');
  const ref    = searchParams.get('ref');
  const amount = parseFloat(searchParams.get('amount')) || 0;

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isRtl = i18n.language === 'ar';
  const direction = isRtl ? 'rtl' : 'ltr';

  // Données bancaires (à adapter selon ton vrai RIB)
  const bankDetails = {
    iban: 'TN59 0803 3012 0710 0080 0665',
    bank: 'BIAT',
    accountHolder: 'JAAFAR MOHAMED ALI',
    swiftCode: 'BIATTNTT',
  };

  useEffect(() => {
    if (!subId) {
      toast.error(t('payment_pending_subscription.missing_subscription_id'));
      navigate('/subscription');
      return;
    }

    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          toast.error(t('payment_pending_subscription.login_required'));
          navigate('/login');
          return;
        }

        const res = await axios.get(
          `${config.SITE_API_URL}/subscriptionById/${subId}`,
          { headers: { Authorization: `Bearer ${token}`,
        'x-api-key': config.SITE_API_KEY } }
        );

        setSubscriptionData(res.data);
      } catch (err) {
        console.error(err);
        toast.error(
          err.response?.data?.message ||
          t('payment_pending_subscription.fetch_error')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [subId, navigate, t]);

  const handleCopyReference = () => {
    if (ref) {
      navigator.clipboard.writeText(ref);
      alert(isRtl ? 'تم نسخ الرقم المرجعي' : 'Référence copiée !');
    }
  };

  const handleGoToDashboard = () => navigate('/dashboard');
const currency = i18n.language === 'fr' ? 'DT' : 'دينار';
  return (
    <div className="min-h-screen bg-gray-50" style={{ direction }}>
      {/* Fond décoratif subtil */}
      <div className="fixed inset-0 pointer-events-none opacity-10 overflow-hidden">
        <div
          className="absolute top-20 right-20 w-80 h-80 rounded-full blur-3xl"
          style={{ backgroundColor: '#f29f2f' }}
        />
        <div
          className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl"
          style={{ backgroundColor: '#0879bd' }}
        />
      </div>

      <m.section className="relative z-10 py-12 min-md:py-20" {...fadeIn}>
        <Container>
          <Row className="justify-center">
            <Col lg={9} xl={8}>
              {/* En-tête statut */}
              <div className="text-center mb-12">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg"
                  style={{ backgroundColor: '#e8f5e9' }}
                >
                  <span className="text-xxxl">⏳</span>
                </div>
                <h1 className="text-lg min-md:text-xxlg font-bold mb-4" style={{ color: '#003479' }}>
                  {t('payment_pending_subscription.title')}
                </h1>
                <p className="text-lg text-gray-600">
                  {t('payment_pending_subscription.order_ref')}{' '}
                  <span className="font-bold" style={{ color: '#0879bd' }}>
                    {ref || subId || '—'}
                  </span>{' '}
                  — <span style={{ color: '#f29f2f' }} className="font-bold">
                    {t('payment_pending_subscription.status_pending')}
                  </span>
                </p>
              </div>

              {/* Bloc montant principal */}
              <div
                className="rounded-3xl p-10 min-md:p-12 mb-10 shadow-2xl text-center"
                style={{
                  background: 'linear-gradient(135deg, #fef8e7 0%, #fff9f0 100%)',
                  border: '3px solid #f29f2f',
                }}
              >
                <p className="text-xl text-gray-700 mb-4">
                  {t('payment_pending_subscription.amount_to_transfer')}
                </p>
                <h2 className="text-xlg min-md:text-xxxl font-extrabold" style={{ color: '#003479' }}>
                  {amount.toFixed(3)} {currency}
                </h2>
              </div>

              {/* Bloc instructions + détails bancaires */}
              <div
                className="rounded-3xl shadow-2xl overflow-hidden mb-10 bg-white"
              >
                <div
                  className="px-8 min-md:px-12 py-8 text-center min-md:text-start"
                  style={{ backgroundColor: '#f0f8ff', borderBottom: '3px solid #e5e7eb' }}
                >
                  <h3 className="text-xxl font-bold" style={{ color: '#003479' }}>
                    {t('payment_pending_subscription.bank_transfer_instructions')}
                  </h3>
                </div>

                <div className="px-8 min-md:px-12 py-10 min-md:py-12 space-y-10">
                  {/* Référence à copier */}
                  <div>
                    <h4 className="text-xlg font-bold mb-4" style={{ color: '#0879bd' }}>
                      {t('payment_pending_subscription.reference_required')}
                    </h4>
                    <div
                      className="p-6 rounded-2xl border-2 flex flex-col sm:flex-row items-center justify-between gap-4"
                      style={{ borderColor: '#f29f2f', backgroundColor: '#fffbf5' }}
                    >
                      <code
                        className="font-mono text-xxlg min-md:text-xl font-bold break-all text-center sm:text-start"
                        style={{ color: '#003479' }}
                      >
                        {ref || '—'}
                      </code>
                    <p className="text-sm text-gray-600 text-center sm:text-start">
                      {t('payment_pending_subscription.reference_note')}
                    </p>
                    </div>
                  </div>

                  {/* Détails bancaires */}
                  <div>
                    <h4 className="text-xlg font-bold mb-5" style={{ color: '#0879bd' }}>
                      {t('payment_pending_subscription.bank_details')}
                    </h4>
                    <div className="grid min-md:grid-cols-2 gap-5">
                      {[
                        { label: t('payment_pending_subscription.iban'), value: bankDetails.iban },
                        { label: t('payment_pending_subscription.bank_name'), value: bankDetails.bank },
                        { label: t('payment_pending_subscription.account_holder'), value: bankDetails.accountHolder },
                        { label: t('payment_pending_subscription.swift'), value: bankDetails.swiftCode },
                      ].map((item, index) => (
                        <div
                          key={index}
                          className="p-5 rounded-xl border shadow-sm"
                          style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
                        >
                          <p className="text-sm font-semibold mb-2" style={{ color: '#0879bd' }}>
                            {item.label}
                          </p>
                          <code className="text-base font-mono break-all" style={{ color: '#003479' }}>
                            {item.value}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Informations importantes */}
                  <div
                    className="p-7 rounded-2xl border-l-4"
                    style={{
                      borderColor: '#f29f2f',
                      backgroundColor: '#fff9f5',
                    }}
                  >
                    <h4 className="text-xl font-bold mb-4" style={{ color: '#003479' }}>
                      {t('payment_pending_subscription.important_info')}
                    </h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending_subscription.note_reference')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending_subscription.note_delay')}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span style={{ color: '#f29f2f', fontSize: '1.4rem' }}>✓</span>
                        <span>{t('payment_pending_subscription.note_activation')}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bouton retour */}
              <div className="text-center">
                <button
                  onClick={handleGoToDashboard}
                  className="w-full sm:w-auto px-12 py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 hover:shadow-2xl hover:scale-105"
                  style={{
                    backgroundColor: '#003479',
                    boxShadow: '0 10px 30px rgba(0, 52, 121, 0.25)',
                  }}
                >
                  {t('payment_pending_subscription.back_to_dashboard')}
                </button>
              </div>

            </Col>
          </Row>
        </Container>
      </m.section>
    </div>
  );
}